import {
  AnalyticsEventIdMessage,
  MessageFromParent,
  MessageFromParentType,
  MessageToParent,
  MessageToParentType,
} from '@stripe-internal/support-conversations-controller';

import {devLog} from '../util/dev';
import {ParentMessageBus, ParentMessageListenerFn} from './ParentMessageBus';

declare global {
  interface Window {
    __ASSISTANT_MOBILE_BRIDGE?: {
      postMessage: (_message: string) => void;
    };
    webkit?: {
      messageHandlers?: {
        [key: string]: {
          postMessage: (message: string) => void;
        };
      };
    };
  }
}

export function createMobileMessageBus({
  audience,
  entryPoint,
}: {
  audience?: string;
  entryPoint?: string;
}): ParentMessageBus {
  const listeners: ParentMessageListenerFn[] = [];

  function addListener(listener: ParentMessageListenerFn) {
    listeners.push(listener);
  }

  function removeListener(listener: ParentMessageListenerFn) {
    const index = listeners.findIndex((l) => l === listener);
    if (index >= 0) {
      listeners.splice(index, 1);
    }
  }

  function post(message: MessageToParent) {
    const wrapped = {
      ...message,
      audience,
      entryPoint,
    };

    devLog('Posting to parent', {message: wrapped});
    const messageName = 'universalHelpMessage.v1';
    const messageHandler = window?.webkit?.messageHandlers?.[messageName];
    // https://go/exceptions/EXCEPTION-6405
    // nosemgrep: ban-postmessage-calls
    messageHandler?.postMessage(JSON.stringify({payload: wrapped}));
  }

  function request(
    message: MessageToParent,
    expectedResponseType: MessageFromParentType,
  ) {
    const requestTimeoutInMilliseconds = 500;

    return new Promise<MessageFromParent>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      function requestHandler(fromParent: MessageFromParent) {
        if (fromParent.type === expectedResponseType) {
          clearTimeout(timeoutId);
          removeListener(requestHandler);
          resolve(fromParent);
        }
      }

      timeoutId = setTimeout(() => {
        removeListener(requestHandler);
        reject(new Error('Timed out waiting for a response'));
      }, requestTimeoutInMilliseconds);

      addListener(requestHandler);
      post(message);
    });
  }

  async function requestAnalyticsEventId(): Promise<string | null> {
    try {
      const result = await request(
        {
          type: MessageToParentType.RequestAnalyticsEventId,
        },
        MessageFromParentType.AnalyticsEventId,
      );
      return (result as AnalyticsEventIdMessage).data.analyticsEventId;
    } catch {
      return null;
    }
  }

  function handler(msg: string) {
    try {
      if (msg === 'test-response') {
        post({
          type: MessageToParentType.RequestAction,
          data: {
            name: 'init-app-response-ack',
          },
        });
      }
      const data = JSON.parse(msg);
      if (data) {
        listeners.slice().forEach((l) => l(data));
      }
    } catch (e) {
      devLog('Error parsing message from mobile bridge', {msg, e});
    }
  }
  window.__ASSISTANT_MOBILE_BRIDGE = {
    postMessage: (msg: string) => handler(msg),
  };

  return {
    post,
    request,
    requestAnalyticsEventId,
    addListener,
    removeListener,
  };
}
